import {
  CSGetSelectedEntityId as GET_SELECTED_ENTITY_ID,
  EntityUser,
  Entity,
  Contact,
  AutocompleteResult,
  ContactStatus,
  AutocompleteType,
  servicesByEntity as SERVICES_BY_ENTITY,
  ServiceStatus,
  GET_SIGNERS,
  GET_CLIENT_CONTACTS,
  GET_CONTACTS_AND_VERIFIED_ENTITY,
  entityUsersByFirmId,
  EntityUserRole,
  CSGetentityUsersByEntityId,
  JobTags,
} from '@admiin-com/ds-graphql';

import {
  autocompleteResultsByType as AUTOCOMPLETE_RESULTS_BY_TYPE,
  contactsByEntity as CONTACTS_BY_ENTITY,
} from '@admiin-com/ds-graphql';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import React from 'react';
import { AutoCompleteDataType } from './AutoCompleteLookupV2';
import { useUserId } from '../../hooks/useCurrentUser/useCurrentUser';
import { toTitleCase } from '../../helpers/string';
import { convertToBackendService } from '../../helpers/tasks';
import { useCurrentEntityId } from '../../hooks/useSelectedEntity/useSelectedEntity';

interface useLookupServiceProps {
  type: AutoCompleteDataType;
  entityId?: string;
  clientGroupCode?: string;
}
export const useLookupService = ({
  type,
  entityId: entityIdProp,
  clientGroupCode,
}: useLookupServiceProps) => {
  const { data: selectedEntityIdData } = useQuery(gql(GET_SELECTED_ENTITY_ID));
  const currentEntityId = selectedEntityIdData?.selectedEntityId;
  const firmId = useCurrentEntityId();
  const entityId = entityIdProp ?? currentEntityId;

  const [getContacts, { loading: loadingContacts }] = useLazyQuery(
    gql(CONTACTS_BY_ENTITY)
  );
  const [getEntityUsers, { loading: loadingEntityUsers }] = useLazyQuery(
    gql(CSGetentityUsersByEntityId)
  );
  const [getEntities, { loading: loadingEntities }] = useLazyQuery(
    gql(CSGetentityUsersByEntityId)
  );

  const [servicesByEntity, { loading: loadingServices }] = useLazyQuery(
    gql(SERVICES_BY_ENTITY)
  );

  const [getSigners, { loading: loadingSigner }] = useLazyQuery(
    gql(GET_SIGNERS)
  );

  const [getClientContacts, { loading: loadingClientContacts }] = useLazyQuery(
    gql(GET_CLIENT_CONTACTS)
  );

  const [
    getEntitiesWithClientGroupCode,
    { loading: loadingEntitiesWithClientGroupCode },
  ] = useLazyQuery(gql(entityUsersByFirmId));

  const userId = useUserId();
  // TODO: Add label for each item

  const [
    getContactsAndVerifiedEntity,
    { loading: loadingContactsAndVerifiedEntity },
  ] = useLazyQuery(gql(GET_CONTACTS_AND_VERIFIED_ENTITY));

  const [getAutocompleteResults, { loading: loadingAutocompleteResults }] =
    useLazyQuery(gql(AUTOCOMPLETE_RESULTS_BY_TYPE));

  const searchContactsAndVerifiedEntities = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getContactsAndVerifiedEntity({
        variables: {
          entityId,
          type: AutocompleteType.ENTITY,
          searchName: value.toLowerCase(),
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return [
        ...(
          data?.data?.contactsByEntity?.items?.filter(
            (contact: Contact) => contact.status !== ContactStatus.ARCHIVED
          ) ?? []
        )
          .map((item: Contact) => ({
            ...item,
            searchType: 'Contacts',
          }))
          .slice(0, 5),
        ...(data?.data?.autocompleteResultsByType?.items ?? [])
          .map((item: AutocompleteResult) => ({
            ...item,
            searchType: 'AutoCompleteResults',
          }))
          .slice(0, 5),
      ];
    },
    [entityId, getContactsAndVerifiedEntity]
  );
  const searchSigners = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getSigners({
        variables: {
          entityId,
          searchName: value?.toLocaleLowerCase(),
          //TODO: filter by email / first / last name
          //filter: {
          //  or: [
          //    { email: { exists: true } },
          //    { firstName: { exists: true } },
          //    { lastName: { exists: true } },
          //  ],
          //}
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });

      return [
        ...(
          data?.data?.contactsByEntity?.items?.filter(
            (contact: Contact) => contact.status !== ContactStatus.ARCHIVED
          ) ?? []
        )
          .map((item: Contact) => ({
            ...item,
            searchType: 'CONTACT',
            name:
              item.firstName && item.lastName
                ? `${item.firstName} ${item.lastName}`
                : item.name,
            secondName: item.name ?? 'Contact',
          }))
          .slice(0, 3),
        ...(data?.data?.entityUsersByEntityId?.items ?? [])
          .map((item: EntityUser) => ({
            ...item,
            searchType: 'ENTITY_USER',
            email: item.invitedEmail,
            name: `${item.firstName} ${item.lastName}`,
            secondName: toTitleCase(item.entitySearchName ?? 'Entity User'),
          }))
          .filter((item: EntityUser) => item.userId !== userId)
          .slice(0, 3),
      ];
    },
    [entityId, getSigners, userId]
  );

  const searchContacts = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getContacts({
        variables: {
          entityId,
          searchName: value,
          //filter: {
          //  searchName: {
          //    contains: value.toLowerCase(),
          //  },
          //  status: {
          //    eq: ContactStatus.ACTIVE,
          //  },
          //},
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.contactsByEntity?.items
          ?.filter(
            (contact: Contact) => contact.status !== ContactStatus.ARCHIVED
          )
          .slice(0, 5) ?? []
      );
    },
    [entityId, getContacts]
  );

  const searchEntityUsers = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getEntityUsers({
        variables: {
          entityId,
          searchName: value.toLowerCase(),
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return data?.data?.entityUsersByEntityId?.items?.slice(0, 5) ?? [];
    },
    [entityId, getEntityUsers]
  );

  const searchClientPartner = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getEntityUsers({
        variables: {
          entityId,
          searchName: value.toLowerCase(),
          limit: 1000,
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.entityUsersByEntityId?.items
          ?.filter((item: EntityUser) =>
            item.jobTags?.includes(JobTags.PARTNER)
          )
          ?.slice(0, 5) ?? []
      );
    },
    [entityId, getEntityUsers]
  );

  const searchClientManager = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getEntityUsers({
        variables: {
          entityId,
          searchName: value.toLowerCase(),
          limit: 1000,
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.entityUsersByEntityId?.items
          ?.filter((item: EntityUser) =>
            item.jobTags?.includes(JobTags.MANAGER)
          )
          ?.slice(0, 5) ?? []
      );
    },
    [entityId, getEntityUsers]
  );

  const searchEntitiesWithClientGroupCode = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getEntitiesWithClientGroupCode({
        variables: {
          firmId: firmId,
          searchName: value.toLowerCase(),
          clientGroupCode: clientGroupCode,
          roles: [EntityUserRole.ACCOUNTANT],
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.entityUsersByFirmId?.items
          ?.slice(0, 5)
          .map((item: EntityUser) => item.entity) ?? []
      );
    },
    [firmId, clientGroupCode, getEntitiesWithClientGroupCode]
  );

  const searchServices = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await servicesByEntity({
        variables: {
          entityId,
          filter: {
            searchName: {
              contains: value?.toLowerCase() ?? '',
            },
            status: {
              eq: ServiceStatus.ACTIVE,
            },
          },
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.servicesByEntity?.items
          ?.slice(0, 5)
          .map(convertToBackendService) ?? []
      );
    },
    [entityId, servicesByEntity]
  );

  const searchEntities = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getEntities({
        variables: {
          limit: 5,
          entityId,
          searchName: value.toLowerCase(),
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return (
        data?.data?.entityUsersByUser?.items
          ?.map((entityUser: EntityUser) => entityUser.entity)
          ?.filter((entity: Entity) => entity !== null)
          ?.slice(0, 5) || []
      );
    },
    [entityId, getEntities]
  );

  const searchAutocompleteResults = React.useCallback(
    async (value: string, refetch = false) => {
      const data = await getAutocompleteResults({
        variables: {
          type: AutocompleteType.ENTITY,
          searchName: value.toLowerCase(),
        },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return data?.data?.autocompleteResultsByType?.items?.slice(0, 5) ?? [];
    },
    [getAutocompleteResults]
  );

  const searchClientContacts = React.useCallback(
    async (value: string, refetch = false) => {
      const { data } = await getClientContacts({
        variables: { entityId, searchName: value.toLowerCase() },
        fetchPolicy: refetch ? 'network-only' : 'cache-first',
        nextFetchPolicy: refetch ? 'network-only' : 'cache-first',
      });
      return [
        ...(data?.entityUsersByFirmId?.items ?? [])
          .map((item: EntityUser) => ({
            ...item.entity,
            searchType: 'Clients',
            taskSendSMS: item.taskSendSMS,
          }))
          .slice(0, 5),
        ...(data?.contactsByEntity?.items ?? [])
          .map((item: Contact) => ({
            ...item,
            searchType: 'Contacts',
          }))
          .slice(0, 5),
      ];
    },
    [entityId, getClientContacts]
  );

  if (type === 'Contact') {
    return { lookup: searchContacts, loading: loadingContacts };
  }
  if (type === 'ContactsAndVerifiedEntity') {
    return {
      lookup: searchContactsAndVerifiedEntities,
      loading: loadingContactsAndVerifiedEntity,
    };
  } else if (type === 'Entity' && !clientGroupCode) {
    return { lookup: searchEntities, loading: loadingEntities };
  } else if (type === 'Entity' && clientGroupCode) {
    return {
      lookup: searchEntitiesWithClientGroupCode,
      loading: loadingEntitiesWithClientGroupCode,
    };
  } else if (type === 'EntityUser') {
    return { lookup: searchEntityUsers, loading: loadingEntityUsers };
  } else if (type === 'ClientPartner') {
    return { lookup: searchClientPartner, loading: loadingEntityUsers };
  } else if (type === 'ClientManager') {
    return { lookup: searchClientManager, loading: loadingEntityUsers };
  } else if (type === 'AutocompleteResults') {
    return {
      lookup: searchAutocompleteResults,
      loading: loadingAutocompleteResults,
    };
  } else if (type === 'Service') {
    return {
      lookup: searchServices,
      loading: loadingServices,
    };
  } else if (type === 'Signer') {
    return {
      lookup: searchSigners,
      loading: loadingSigner,
    };
  } else if (type === 'ClientContacts') {
    return {
      lookup: searchClientContacts,
      loading: loadingClientContacts,
    };
  }
  return { lookup: undefined, loading: false };
};
